// api.ts
// Tours API client – uses shared Axios `request` wrapper from client.ts

import { request } from "@/api/client";
import { PATHS } from "@/api/constants";

import type {
  TourHeader,
  TourDetail,
  CreateTourWithDetailsPayload,
  UpdateTourWithDetailsPayload,
  ListToursData,
  ListToursQueryParams,
  ApiSuccessResponse,
  ApiSuccessNoDataResponse,
  AiTourImportResult,
} from "./types";


const TOURS_BASE_PATH = PATHS.tours.base;

/* =========================================================
 * AI IMPORT – POST /api/v1/tours/ai-import (multipart)
 * ======================================================= */

/** Select-option lists the AI should pick from (tour types, hotel categories…). */
export type AiImportOptions = {
  tourTypes?: string[];
  hotelCategories?: string[];
  roomTypes?: string[];
};

/** Async import job status returned by the polling endpoint. */
type AiImportJob = {
  id: string;
  status: "PENDING" | "PROCESSING" | "DONE" | "FAILED";
  result: AiTourImportResult | null;
  error: string | null;
};

const sleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

// Polling cadence/cap for the background import job. Each poll is a short
// request, so it never trips gateway/load-balancer timeouts.
const POLL_INTERVAL_MS = 3_000;
const POLL_TIMEOUT_MS = 5 * 60_000;

/**
 * Upload one or more itinerary documents (PDF / Word / Excel / images) and get
 * back structured tour data to prefill the create-tour form. `options` lets the
 * AI choose from the system's existing dropdown values.
 *
 * Runs asynchronously: the upload enqueues a job (returns fast), then we poll
 * until the AI finishes — so the long OpenAI call never holds the HTTP request
 * open past the gateway timeout.
 */
export async function aiImportTour(
  files: File[],
  options: AiImportOptions = {}
): Promise<AiTourImportResult> {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }
  formData.append("options", JSON.stringify(options));

  // 1) Enqueue — short request that returns a job id.
  const submit = await request<ApiSuccessResponse<{ jobId: string }>>({
    method: "POST",
    url: PATHS.tours.aiImport,
    data: formData,
    // Let the browser set the multipart boundary.
    headers: { "Content-Type": "multipart/form-data" },
    timeout: 60_000,
  });
  const jobId = submit.data?.jobId;
  if (!jobId) throw new Error("Could not start the import. Please try again.");

  // 2) Poll until the job is DONE or FAILED.
  const startedAt = Date.now();
  for (;;) {
    await sleep(POLL_INTERVAL_MS);

    const res = await request<ApiSuccessResponse<AiImportJob>>({
      method: "GET",
      url: PATHS.tours.aiImportStatus(jobId),
      timeout: 30_000,
    });
    const job = res.data;

    if (job?.status === "DONE" && job.result) return job.result;
    if (job?.status === "FAILED") {
      throw new Error(job.error || "Failed to process the itinerary.");
    }
    if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
      throw new Error(
        "The itinerary is taking too long to process. Please try again."
      );
    }
  }
}

/* =========================================================
 * Internal helper types (success-only responses)
 * ======================================================= */

type ListToursSuccessResponse = ApiSuccessResponse<ListToursData>;
type GetTourDetailSuccessResponse = ApiSuccessResponse<TourDetail>;
type CreateTourSuccessResponse = ApiSuccessResponse<TourHeader>;
type UpdateTourSuccessResponse = ApiSuccessResponse<TourHeader>;
type DeleteTourSuccessResponse = ApiSuccessNoDataResponse;

/* =========================================================
 * LIST – GET /api/v1/tours
 * ======================================================= */

/**
 * Fetch paginated list of tours (header-only).
 */
export async function listTours(
  params: ListToursQueryParams = {}
): Promise<ListToursData> {
  const res = await request<ListToursSuccessResponse>({
    url: TOURS_BASE_PATH,
    method: "GET",
    params,
  });

  return res.data;
}

/* =========================================================
 * GET BY ID – GET /api/v1/tours/:id
 * ======================================================= */

/**
 * Fetch a single tour with all nested details.
 */
export async function getTourById(id: number): Promise<TourDetail> {
  const res = await request<GetTourDetailSuccessResponse>({
    url: `${TOURS_BASE_PATH}/${id}`,
    method: "GET",
  });

  return res.data;
}

/* =========================================================
 * CREATE – POST /api/v1/tours
 * ======================================================= */

/**
 * Create a tour header + children in one transaction.
 * Returns the created header (no children).
 */
export async function createTour(
  payload: CreateTourWithDetailsPayload
): Promise<TourHeader> {
  const res = await request<CreateTourSuccessResponse>({
    url: TOURS_BASE_PATH,
    method: "POST",
    data: payload,
  });

  return res.data;
}

/* =========================================================
 * UPDATE – PUT /api/v1/tours/:id
 * ======================================================= */

/**
 * Update a tour header + selectively replace its children blocks.
 * Returns the updated header (no children).
 */
export async function updateTour(
  id: number,
  payload: UpdateTourWithDetailsPayload
): Promise<TourHeader> {
  const res = await request<UpdateTourSuccessResponse>({
    url: `${TOURS_BASE_PATH}/${id}`,
    method: "PUT",
    data: payload,
  });

  return res.data;
}

/* =========================================================
 * DELETE – DELETE /api/v1/tours/:id
 * ======================================================= */

/**
 * Soft delete a tour + all children.
 * Returns only a success message; callers usually just invalidate queries.
 */
export async function deleteTour(id: number): Promise<string> {
  const res = await request<DeleteTourSuccessResponse>({
    url: `${TOURS_BASE_PATH}/${id}`,
    method: "DELETE",
  });

  return res.message;
}
